import { Home, Lightbulb, Star, Gamepad2, Heart, Settings, ChevronRight } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBaby } from '@/contexts/BabyContext';
import { formatBabyAge } from '@/lib/baby-age';
import babyBoyAvatar from '@/assets/baby_boy.webp';
import babyGirlAvatar from '@/assets/baby-girl-avatar.webp';

const TABS = [
  { id: 'today',      label: 'Today',      path: '/',           Icon: Home },
  { id: 'knowledge',  label: 'Knowledge',  path: '/insights',   Icon: Lightbulb },
  { id: 'milestones', label: 'Milestones', path: '/milestones', Icon: Star },
  { id: 'activities', label: 'Activities', path: '/activities', Icon: Gamepad2 },
  { id: 'health',     label: 'Health',     path: '/health',     Icon: Heart },
];

const DesktopSidebar = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { activeBaby, babies } = useBaby();

  const activeId =
    pathname === '/'             ? 'today'
    : pathname === '/insights'   ? 'knowledge'
    : pathname === '/milestones' ? 'milestones'
    : pathname === '/activities' ? 'activities'
    : pathname === '/health'     ? 'health'
    : '';

  const ageLabel = activeBaby?.dob ? formatBabyAge(activeBaby.dob) : null;

  return (
    <aside
      className="hidden lg:flex flex-col fixed top-0 left-0 h-full z-40"
      style={{
        width: 220,
        background: '#FAFAF9',
        borderRight: '1px solid hsl(var(--border))',
      }}
    >
      {/* numaa wordmark */}
      <div className="px-5 pt-6 pb-3 flex-shrink-0">
        <span
          style={{
            fontSize: 18,
            fontWeight: 800,
            letterSpacing: '-0.03em',
            color: 'hsl(var(--primary))',
          }}
        >
          numaa
        </span>
      </div>

      {/* Baby pill */}
      {activeBaby && (
        <button
          onClick={() => babies.length > 1 ? navigate('/settings') : undefined}
          className="mx-4 mb-4 p-3 rounded-xl flex items-center gap-2.5 transition-colors text-left flex-shrink-0"
          style={{
            background: 'white',
            border: '1px solid hsl(var(--border))',
            boxShadow: 'var(--shadow-card)',
          }}
        >
          <img
            src={activeBaby.photoURL || (activeBaby.gender === 'boy' ? babyBoyAvatar : babyGirlAvatar)}
            alt={activeBaby.name}
            className="w-9 h-9 rounded-full object-cover flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <p className="text-foreground leading-none truncate" style={{ fontSize: 14, fontWeight: 600 }}>{activeBaby.name}</p>
            {ageLabel && (
              <p className="text-caption text-muted-foreground mt-0.5">{ageLabel}</p>
            )}
          </div>
          {babies.length > 1 && (
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
          )}
        </button>
      )}

      {/* Nav items */}
      <nav className="flex-1 px-2 space-y-0.5 overflow-y-auto">
        {TABS.map(({ id, label, path, Icon }) => {
          const isActive = activeId === id;
          return (
            <button
              key={id}
              onClick={() => navigate(path)}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-[10px] transition-colors"
              style={{
                fontSize: 14,
                fontWeight: isActive ? 600 : 500,
                color: isActive ? 'hsl(var(--primary))' : 'hsl(var(--nav-inactive))',
                background: isActive ? 'hsl(var(--primary) / 0.1)' : 'transparent',
              }}
            >
              <Icon
                className="flex-shrink-0"
                style={{
                  width: 20,
                  height: 20,
                  color: isActive ? 'hsl(var(--primary))' : 'hsl(var(--nav-inactive))',
                }}
              />
              {label}
            </button>
          );
        })}
      </nav>

      {/* Settings */}
      <div
        className="px-2 pb-6 pt-3 flex-shrink-0"
        style={{ borderTop: '1px solid hsl(var(--border))' }}
      >
        <button
          onClick={() => navigate('/settings')}
          className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-[10px] transition-colors"
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: 'hsl(var(--nav-inactive))',
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLButtonElement).style.background = 'hsl(var(--muted))';
            (e.currentTarget as HTMLButtonElement).style.color = 'hsl(var(--foreground))';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            (e.currentTarget as HTMLButtonElement).style.color = 'hsl(var(--nav-inactive))';
          }}
        >
          <Settings style={{ width: 20, height: 20 }} />
          Settings
        </button>
      </div>
    </aside>
  );
};

export default DesktopSidebar;
